import { FC, memo } from 'react';
import { PresetColorKey } from 'antd/es/theme/internal';
import { Button, Card } from 'shared/ui';
import { EngineListItem } from 'entities/engine';
import { BadgedLabel } from 'shared/ui/BadgedLabel';
import { EngineStatusInfo } from 'pages/Engines/hooks/useEngines';
import styles from './EngineCard.module.scss';

interface EngineCardProps {
  engine: EngineListItem;
  onDelete: (id: number) => void;
  onViewDetails: (engine: EngineListItem) => void;
  onRequestMaintenance: (engine: EngineListItem) => void;
  getStatusInfo: (engine: EngineListItem) => EngineStatusInfo;
  formatDate: (dateString: string) => string;
}

const getMaintenanceDueLabel = ({ weeksUntilMaintenance, weeksOverdue }: EngineStatusInfo): string => {
  if (weeksOverdue !== null) {
    return weeksOverdue > 0 ? `Просрочено на ${weeksOverdue} нед.` : 'Просрочено';
  }
  if (weeksUntilMaintenance === null) {
    return 'Не указано';
  }
  return `${weeksUntilMaintenance} недель`;
};

export const EngineCard: FC<EngineCardProps> = memo(({
  engine,
  onDelete,
  onViewDetails,
  onRequestMaintenance,
  getStatusInfo,
  formatDate,
}) => {
  const statusInfo = getStatusInfo(engine);

  return (
    <Card>
      <div className={styles.cardHeader}>
        <div className={styles.titleSection}>
          <h3 className={styles.engineTitle}>
            {engine.orderCode || 'Заказной код не указан'}
            {engine.comment && ` ${engine.comment}`}
          </h3>
        </div>
        <BadgedLabel
          value={statusInfo.status}
          statusColorMap={{ [statusInfo.status]: statusInfo.color as PresetColorKey }}
          ariaLabel={`Статус двигателя: ${statusInfo.status}`}
        />
      </div>

      <div className={styles.cardDetails}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Серийный номер:</span>
          <span className={styles.detailValue}>{engine.serialNumber || 'Не указан'}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Последнее ТО:</span>
          <span className={styles.detailValue}>
            {engine.lastMaintenance ? formatDate(engine.lastMaintenance) : 'Не указано'}
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Следующее ТО:</span>
          <span className={styles.detailValue}>
            {engine.nextMaintenance ? formatDate(engine.nextMaintenance) : 'Не указано'}
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>До следующего ТО:</span>
          <span className={styles.detailValue}>
            {getMaintenanceDueLabel(statusInfo)}
          </span>
        </div>

        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{
              width: `${statusInfo.progressBarPercent}%`,
              backgroundColor: statusInfo.color,
            }}
          />
        </div>
      </div>

      <div className={styles.cardActions}>
        <Button
          variant="outlined"
          onClick={() => onViewDetails(engine)}
          className={styles.detailsButton}
        >
          Подробнее
        </Button>
        <Button
          onClick={() => onRequestMaintenance(engine)}
          className={styles.actionButton}
          style={{ backgroundColor: statusInfo.color }}
        >
          Запрос на ТО
        </Button>
        {engine.id && (
          <Button
            onClick={() => onDelete(engine.id!)}
            className={styles.deleteButton}
            style={{ backgroundColor: '#ff4d4f', color: 'white' }}
          >
            Удалить
          </Button>
        )}
      </div>
    </Card>
  );
});

EngineCard.displayName = 'EngineCard';
